/**
 * X1NS (X1 Name Service) Integration
 * Resolves X1 blockchain domains (.x1, .xnt, .xen) to addresses and vice versa
 *
 * API Documentation: https://docs.x1ns.xyz/
 * Base URL: https://api.x1ns.xyz
 */

import { log } from '@/utils/logger'

const X1NS_API_BASE = 'https://api.x1ns.xyz/api'

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const REVERSE_CACHE_TTL = 10 * 60 * 1000 // 10 minutes for reverse lookups

// TLDs supported by X1NS
export const X1NS_TLDS = ['.x1', '.xnt', '.xen'] as const
export type X1nsTld = typeof X1NS_TLDS[number]

export interface X1nsDomain {
  name: string
  address: string
}

export interface X1nsProfile {
  owner?: string
  twitter?: string
  discord?: string
  website?: string
  avatar?: string
  bio?: string
  records?: {
    x1?: string
    sol?: string
    eth?: string
    btc?: string
  }
}

export interface X1nsListing {
  domain: string
  domainAddress: string
  escrowAddress: string
  seller: string
  priceLamports: number
  priceXNT: number
  timestamp: number
}

export interface X1nsDomainNFT {
  mint: string
  name: string
  symbol: string
  description: string
  image: string
  collection: string
  attributes: Array<{ trait_type: string; value: string | number }>
  creator: string
  owner: string
  supply: number
  decimals: number
  recentActivity: string
  verified: boolean
  isX1nsDomain: boolean
  priceXNT?: number
}

interface CacheEntry<T> {
  data: T
  timestamp: number
}

class X1nsService {
  // Cache for domain -> address resolution
  private domainCache: Map<string, CacheEntry<string | null>> = new Map()
  // Cache for address -> primary domain (reverse lookup)
  private reverseCache: Map<string, CacheEntry<string | null>> = new Map()
  // Cache for all domains owned by an address
  private domainsCache: Map<string, CacheEntry<X1nsDomain[]>> = new Map()
  // Pending requests to prevent duplicate API calls
  private pendingRequests: Map<string, Promise<any>> = new Map()

  /**
   * Check if a string looks like an X1NS domain
   */
  isX1nsDomain(input: string): boolean {
    if (!input || typeof input !== 'string') return false
    const lower = input.toLowerCase()
    return X1NS_TLDS.some(tld => lower.endsWith(tld))
  }

  /**
   * Extract TLD from a domain name
   */
  getTld(domain: string): X1nsTld | null {
    const lower = domain.toLowerCase()
    for (const tld of X1NS_TLDS) {
      if (lower.endsWith(tld)) return tld
    }
    return null
  }

  /**
   * Resolve a domain name to its owner address
   * Returns null if domain is not registered
   */
  async resolveDomain(domain: string): Promise<string | null> {
    const normalizedDomain = domain.toLowerCase().trim()

    // Check cache first
    const cached = this.domainCache.get(normalizedDomain)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data
    }

    // Check for pending request
    const pendingKey = `resolve:${normalizedDomain}`
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey)
    }

    const request = this.fetchResolveDomain(normalizedDomain)
    this.pendingRequests.set(pendingKey, request)

    try {
      const result = await request
      this.domainCache.set(normalizedDomain, { data: result, timestamp: Date.now() })
      return result
    } finally {
      this.pendingRequests.delete(pendingKey)
    }
  }

  private async fetchResolveDomain(domain: string): Promise<string | null> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/resolve/${encodeURIComponent(domain)}`)

      if (!response.ok) {
        if (response.status === 404) {
          return null // Domain not registered
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      return data.owner || data.address || null
    } catch (error) {
      log.warn(`X1NS: Failed to resolve domain ${domain}:`, error)
      return null
    }
  }

  /**
   * Get the primary domain for a wallet address (reverse lookup)
   * Returns null if no primary domain is set
   */
  async getPrimaryDomain(address: string): Promise<string | null> {
    if (!address || typeof address !== 'string') return null

    const normalizedAddress = address.trim()

    // Check cache first
    const cached = this.reverseCache.get(normalizedAddress)
    if (cached && Date.now() - cached.timestamp < REVERSE_CACHE_TTL) {
      return cached.data
    }

    // Check for pending request
    const pendingKey = `primary:${normalizedAddress}`
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey)
    }

    const request = this.fetchPrimaryDomain(normalizedAddress)
    this.pendingRequests.set(pendingKey, request)

    try {
      const result = await request
      this.reverseCache.set(normalizedAddress, { data: result, timestamp: Date.now() })
      return result
    } finally {
      this.pendingRequests.delete(pendingKey)
    }
  }

  private async fetchPrimaryDomain(address: string): Promise<string | null> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/primary/${encodeURIComponent(address)}`)

      if (!response.ok) {
        if (response.status === 404) {
          return null // No primary domain set
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      return data.domain || data.name || null
    } catch (error) {
      log.warn(`X1NS: Failed to get primary domain for ${address}:`, error)
      return null
    }
  }

  /**
   * Get all domains owned by an address
   */
  async getDomainsByOwner(address: string): Promise<X1nsDomain[]> {
    if (!address || typeof address !== 'string') return []

    const normalizedAddress = address.trim()

    // Check cache first
    const cached = this.domainsCache.get(normalizedAddress)
    if (cached && Date.now() - cached.timestamp < REVERSE_CACHE_TTL) {
      return cached.data
    }

    // Check for pending request
    const pendingKey = `domains:${normalizedAddress}`
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey)
    }

    const request = this.fetchDomainsByOwner(normalizedAddress)
    this.pendingRequests.set(pendingKey, request)

    try {
      const result = await request
      this.domainsCache.set(normalizedAddress, { data: result, timestamp: Date.now() })
      return result
    } finally {
      this.pendingRequests.delete(pendingKey)
    }
  }

  private async fetchDomainsByOwner(address: string): Promise<X1nsDomain[]> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/reverse/${encodeURIComponent(address)}`)

      if (!response.ok) {
        if (response.status === 404) {
          return [] // No domains owned
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()

      // Handle different response formats
      if (Array.isArray(data)) {
        return data.map((d: any) => ({
          name: d.name || d.domain,
          address: d.address || d.domainAddress
        }))
      }

      if (data.domains && Array.isArray(data.domains)) {
        return data.domains.map((d: any) => ({
          name: d.name || d.domain,
          address: d.address || d.domainAddress
        }))
      }

      return []
    } catch (error) {
      log.warn(`X1NS: Failed to get domains for ${address}:`, error)
      return []
    }
  }

  /**
   * Check if a domain is available for registration
   */
  async isDomainAvailable(domain: string): Promise<boolean> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/available/${encodeURIComponent(domain.toLowerCase())}`)

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      return data.available === true
    } catch (error) {
      log.warn(`X1NS: Failed to check availability for ${domain}:`, error)
      return false
    }
  }

  /**
   * Get profile data for a domain
   */
  async getProfile(domain: string): Promise<X1nsProfile | null> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/profile/${encodeURIComponent(domain.toLowerCase())}`)

      if (!response.ok) {
        if (response.status === 404) {
          return null
        }
        throw new Error(`API error: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      log.warn(`X1NS: Failed to get profile for ${domain}:`, error)
      return null
    }
  }

  /**
   * Batch resolve multiple addresses to their primary domains
   * More efficient than individual calls for lists
   */
  async batchGetPrimaryDomains(addresses: string[]): Promise<Map<string, string | null>> {
    const results = new Map<string, string | null>()
    const uncachedAddresses: string[] = []

    // Check cache first
    for (const address of addresses) {
      const cached = this.reverseCache.get(address)
      if (cached && Date.now() - cached.timestamp < REVERSE_CACHE_TTL) {
        results.set(address, cached.data)
      } else {
        uncachedAddresses.push(address)
      }
    }

    // Fetch uncached addresses in parallel (with concurrency limit)
    const BATCH_SIZE = 10
    for (let i = 0; i < uncachedAddresses.length; i += BATCH_SIZE) {
      const batch = uncachedAddresses.slice(i, i + BATCH_SIZE)
      const batchResults = await Promise.all(
        batch.map(addr => this.getPrimaryDomain(addr).then(domain => ({ addr, domain })))
      )

      for (const { addr, domain } of batchResults) {
        results.set(addr, domain)
      }
    }

    return results
  }

  /**
   * Get recent marketplace listings for display in NFT listings
   */
  async getMarketplaceListings(limit: number = 100): Promise<X1nsListing[]> {
    try {
      const response = await fetch(`${X1NS_API_BASE}/marketplace/listings`)

      if (!response.ok) {
        if (response.status === 404) {
          return []
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()

      // Handle different response formats
      const listings = Array.isArray(data) ? data : (data.listings || [])

      return listings.slice(0, limit).map((item: any) => ({
        domain: item.domain,
        domainAddress: item.domainAddress,
        escrowAddress: item.escrowAddress,
        seller: item.seller,
        priceLamports: item.priceLamports,
        priceXNT: item.priceXNT,
        timestamp: item.timestamp
      }))
    } catch (error) {
      log.warn('X1NS: Failed to get marketplace listings:', error)
      return []
    }
  }

  /**
   * Convert X1NS listings to NFT format for display
   */
  convertListingsToNFTFormat(listings: X1nsListing[]): X1nsDomainNFT[] {
    return listings.map(listing => {
      // Extract TLD for categorization
      const tld = this.getTld(listing.domain) || '.x1'
      const nameWithoutTld = listing.domain.replace(tld, '')

      return {
        mint: listing.domainAddress,
        name: listing.domain,
        symbol: 'X1NS',
        description: `X1 Name Service domain: ${listing.domain}`,
        image: '', // X1NS image API not available - uses placeholder with domain name display
        collection: 'X1 Name Service',
        attributes: [
          { trait_type: 'TLD', value: tld },
          { trait_type: 'Name Length', value: nameWithoutTld.length },
          { trait_type: 'Listed Price (XNT)', value: listing.priceXNT || 0 }
        ],
        creator: listing.seller,
        owner: listing.seller,
        supply: 1,
        decimals: 0,
        recentActivity: listing.timestamp ? new Date(listing.timestamp * 1000).toISOString() : new Date().toISOString(),
        verified: true,
        isX1nsDomain: true,
        priceXNT: listing.priceXNT
      }
    })
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.domainCache.clear()
    this.reverseCache.clear()
    this.domainsCache.clear()
  }

  /**
   * Pre-warm cache for a list of addresses (e.g., for transaction lists)
   */
  async warmCache(addresses: string[]): Promise<void> {
    const uniqueAddresses = [...new Set(addresses.filter(Boolean))]
    if (uniqueAddresses.length === 0) return

    // Don't await - let it run in background
    this.batchGetPrimaryDomains(uniqueAddresses).catch(err => {
      log.warn('X1NS: Cache warm failed:', err)
    })
  }
}

// Export singleton instance
export const x1nsService = new X1nsService()
export default x1nsService
